import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  FileText,
  ClipboardCheck,
  Truck,
  Phone,
  CheckCircle2,
  Building2,
  Users,
  TrendingUp,
  ShieldCheck,
  Package,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import Navbar from "../components/nav/Navbar";
import HeroSection from "../components/HeroSection";

const BACKEND_URL = process.env.BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Process steps ───────────────────────────────────────────────────────── */
const PROCESS_STEPS = [
  {
    icon: Phone,
    label: "01 — Consultation",
    title: "Tell us about your operation",
    body: "We start with a discovery call to understand your location, footfall, revenue goals, and any customization needs. No pressure — just clarity.",
    color: "var(--vb-indigo)",
  },
  {
    icon: FileText,
    label: "02 — Custom Quote",
    title: "Receive your tailored quote",
    body: "Within 24 hours you receive a detailed proposal: machine specs, unit pricing, volume discounts, lead time, and delivery costs. All in writing.",
    color: "var(--vb-purple)",
  },
  {
    icon: ClipboardCheck,
    label: "03 — Contract & Deposit",
    title: "Sign and lock in your order",
    body: "Your quote converts to a digital contract via PandaDoc. A deposit secures your production slot. Balance is due before shipment.",
    color: "var(--vb-orchid)",
  },
  {
    icon: Package,
    label: "04 — Production",
    title: "Your machines are built",
    body: "You get a portal link with real-time production status, QC checkpoints, and photo updates. Full transparency from factory floor to freight.",
    color: "var(--vb-fuchsia)",
  },
  {
    icon: Truck,
    label: "05 — Delivery & Setup",
    title: "Shipped and tracked",
    body: "Live freight tracking from the moment it leaves. We handle customs documentation for international orders. Approximately 21 days in transit after production is completed.",
    color: "var(--vb-magenta)",
  },
  {
    icon: ShieldCheck,
    label: "06 — Post-Sale Support",
    title: "We're with you after delivery",
    body: "Every order includes onboarding, remote technical support, and a warranty. Your customer portal stays live for the life of your machine.",
    color: "var(--vb-pink)",
  },
];

/* ── B2B use cases ───────────────────────────────────────────────────────── */
const USE_CASES = [
  {
    icon: Building2,
    title: "Multi-Location Operators",
    body: "Deploying 5+ machines across offices, campuses, or retail chains. Volume pricing, unified management dashboard, and dedicated account support.",
  },
  {
    icon: Users,
    title: "Hospitality & Entertainment",
    body: "Hotels, arcades, event venues, and entertainment centers. Custom branding, cashless acceptance, and arcade-specific machine options.",
  },
  {
    icon: TrendingUp,
    title: "Investment & Passive Income",
    body: "First-time operators and vending investors. We handle the sourcing — you collect the revenue. Simple payback calculators on request.",
  },
];

/* ── FAQ ─────────────────────────────────────────────────────────────────── */
const FAQ = [
  {
    q: "What is the minimum order quantity?",
    a: "Most machines have a MOQ of 1 unit. Volume pricing begins at 3 units for snack/combo machines and 2 units for specialty machines.",
  },
  {
    q: "Can I add custom branding to the machines?",
    a: "Yes. We offer full-wrap vinyl branding, custom button panels, and screen UI customization. Lead times and pricing vary by machine type — include your branding needs in the quote form.",
  },
  {
    q: "How long does delivery take?",
    a: "Most US domestic orders ship within approximately 21 days of production completion. International orders are 4–6 weeks after production, depending on customs. Expedited options are available.",
  },
  {
    q: "Is financing available?",
    a: "We offer net-30 and net-60 payment terms for approved business accounts. Equipment financing partnerships are available for orders over $10,000.",
  },
  {
    q: "What does the warranty cover?",
    a: "Standard 12-month parts and labor warranty. Extended warranties and maintenance contracts are available as add-ons.",
  },
  {
    q: "Can I see a demo or get a sample unit?",
    a: "We can arrange in-person demonstrations at select locations or ship a demo unit (returnable) for qualified orders over $15,000.",
  },
];

/* ── Country list ────────────────────────────────────────────────────────── */
const COUNTRIES = [
  "United States","Canada","United Kingdom","Australia","Germany","France",
  "Netherlands","Sweden","Norway","Denmark","Switzerland","Austria","Belgium",
  "Spain","Italy","Portugal","Ireland","Finland","New Zealand","Singapore",
  "Japan","South Korea","United Arab Emirates","Saudi Arabia","Qatar",
  "South Africa","Nigeria","Kenya","Mexico","Brazil","Argentina","Colombia",
  "Chile","Peru","India","Philippines","Malaysia","Thailand","Indonesia",
  "Vietnam","China","Hong Kong","Taiwan","Israel","Turkey","Poland",
  "Czech Republic","Hungary","Romania","Greece","Other",
];

/* ── Phone normalisation ─────────────────────────────────────────────────── */
// Strips everything except digits, +, and leading spaces.
// Allows country codes (e.g. +1, +44). Removes all other symbols before send.
function normalisePhone(raw) {
  // Keep only digits and a leading +
  const stripped = raw.replace(/[^\d+]/g, "");
  // Ensure only one leading +
  return stripped.replace(/^\++/, "+").replace(/(\+.*?)\+/g, "$1");
}

/* ── Multi-select pill toggle ────────────────────────────────────────────── */
function PillToggle({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
        active
          ? "bg-indigo-600 border-indigo-600 text-white"
          : "border-border text-muted-foreground hover:border-indigo-400 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

/* ── Quote Dialog ────────────────────────────────────────────────────────── */
const EMPTY_FORM = {
  customer_name: "",
  email: "",
  phone: "",
  company_name: "",
  // location broken into parts — composed into `location` string on submit
  city: "",
  state: "",
  country: "United States",
  quantity: 1,
  machine_type: "",
  budget_range: "",
  customization_details: "",
  payment_options: [],      // list of LeadPaymentOption enum values
  additional_features: [],  // list of LeadAdditionalFeature enum values
  source: "Website Form",
};

function B2BQuoteDialog({ open, onOpenChange }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const onSelect = (name, value) => setFormData((p) => ({ ...p, [name]: value }));

  const toggleList = (name, value) =>
    setFormData((p) => ({
      ...p,
      [name]: p[name].includes(value)
        ? p[name].filter((v) => v !== value)
        : [...p[name], value],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Compose location string from parts
      const locationParts = [formData.city, formData.state, formData.country].filter(Boolean);
      const location = locationParts.join(", ");

      // Normalise phone — strip all symbols except leading + and digits
      const phone = normalisePhone(formData.phone);

      // Build payload — only send fields the backend model accepts
      // budget_range and machine_type are optional; omit if empty string
      const payload = {
        customer_name:        formData.customer_name.trim(),
        email:                formData.email.trim(),
        phone,
        company_name:         formData.company_name.trim() || undefined,
        location,
        city:                 formData.city.trim() || undefined,
        state:                formData.state.trim() || undefined,
        country:              formData.country || undefined,
        quantity:             Number(formData.quantity) || 1,
        machine_type:         formData.machine_type || undefined,
        budget_range:         formData.budget_range || undefined,
        customization_details: formData.customization_details.trim() || undefined,
        payment_options:      formData.payment_options,
        additional_features:  formData.additional_features,
        source:               "Website Form",
      };

      // Remove undefined keys so Pydantic doesn't see null where Optional is fine
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      await axios.post(`${API}/quotes`, payload);

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData(EMPTY_FORM);
        onOpenChange(false);
      }, 2800);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" · ")
        : detail || "Failed to submit. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const LBL = "font-mono text-[10px] uppercase tracking-wider text-muted-foreground";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-center"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            Request a Business Quote
          </DialogTitle>
          <p className="font-body text-sm text-muted-foreground text-center mt-1">
            We'll respond within 24 hours with pricing tailored to your needs.
          </p>
        </DialogHeader>

        {submitted ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <CheckCircle2 className="h-14 w-14 text-green-500" />
            <p className="font-heading text-xl text-foreground" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Quote Request Received!
            </p>
            <p className="text-sm text-muted-foreground text-center">
              We'll be in touch within 24 hours with pricing and options.
            </p>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-5 mt-4">

          {/* ── Contact ── */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_name" className={LBL}>Full Name *</Label>
              <Input id="customer_name" name="customer_name" type="text" required
                value={formData.customer_name} onChange={onChange} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="email" className={LBL}>Business Email *</Label>
              <Input id="email" name="email" type="email" required
                value={formData.email} onChange={onChange} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="phone" className={LBL}>Phone * <span className="normal-case opacity-60">(country code ok)</span></Label>
              <Input id="phone" name="phone" type="tel" required
                placeholder="+1 555 000 0000"
                value={formData.phone} onChange={onChange} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="company_name" className={LBL}>Company / Business Name</Label>
              <Input id="company_name" name="company_name" type="text"
                value={formData.company_name} onChange={onChange} className="mt-1" />
            </div>
          </div>

          {/* ── Delivery Location ── */}
          <div>
            <p className={`${LBL} mb-2`}>Delivery Location *</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="city" className={LBL}>City</Label>
                <Input id="city" name="city" type="text" required
                  placeholder="Detroit"
                  value={formData.city} onChange={onChange} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="state" className={LBL}>State / Province</Label>
                <Input id="state" name="state" type="text"
                  placeholder="Michigan"
                  value={formData.state} onChange={onChange} className="mt-1" />
              </div>
              <div>
                <Label className={LBL}>Country</Label>
                <Select value={formData.country} onValueChange={(v) => onSelect("country", v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* ── Machine + Quantity ── */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className={LBL}>Machine Type</Label>
              <Select value={formData.machine_type} onValueChange={(v) => onSelect("machine_type", v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Combo Snack+Drink">Combo Snack + Drink</SelectItem>
                  <SelectItem value="Standard Snack">Standard Snack</SelectItem>
                  <SelectItem value="Refrigerated">Refrigerated</SelectItem>
                  <SelectItem value="Frozen">Frozen</SelectItem>
                  <SelectItem value="Arcade Game">Arcade Game</SelectItem>
                  <SelectItem value="Pinball Machine">Pinball Machine</SelectItem>
                  <SelectItem value="Prize Redemption">Prize Redemption</SelectItem>
                  <SelectItem value="Crane/Claw Machine">Crane / Claw Machine</SelectItem>
                  <SelectItem value="Token Dispenser">Token Dispenser</SelectItem>
                  <SelectItem value="Ticket Dispenser">Ticket Dispenser</SelectItem>
                  <SelectItem value="Custom">Custom / Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="quantity" className={LBL}>Quantity *</Label>
              <Input id="quantity" name="quantity" type="number" min="1" required
                value={formData.quantity} onChange={onChange} className="mt-1" />
            </div>
          </div>

          {/* ── Budget ── */}
          <div>
            <Label className={LBL}>Budget Range</Label>
            <Select value={formData.budget_range} onValueChange={(v) => onSelect("budget_range", v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select range" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Under $5k">Under $5,000</SelectItem>
                <SelectItem value="$5k–$10k">$5,000 – $10,000</SelectItem>
                <SelectItem value="$10k–$20k">$10,000 – $20,000</SelectItem>
                <SelectItem value="$20k–$50k">$20,000 – $50,000</SelectItem>
                <SelectItem value="Over $50k">Over $50,000</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* ── Payment Options ── */}
          <div>
            <Label className={`${LBL} block mb-2`}>Payment Preference</Label>
            <div className="flex flex-wrap gap-2">
              {["Full Payment", "Deposit + Balance", "Financing"].map((opt) => (
                <PillToggle
                  key={opt}
                  label={opt}
                  active={formData.payment_options.includes(opt)}
                  onClick={() => toggleList("payment_options", opt)}
                />
              ))}
            </div>
          </div>

          {/* ── Additional Features ── */}
          <div>
            <Label className={`${LBL} block mb-2`}>Additional Features</Label>
            <div className="flex flex-wrap gap-2">
              {["Card Reader","Touch Screen","Remote Monitoring","Custom Branding","LED Lighting","ADA Compliant"].map((feat) => (
                <PillToggle
                  key={feat}
                  label={feat}
                  active={formData.additional_features.includes(feat)}
                  onClick={() => toggleList("additional_features", feat)}
                />
              ))}
            </div>
          </div>

          {/* ── Details ── */}
          <div>
            <Label htmlFor="customization_details" className={LBL}>Details / Requirements</Label>
            <Textarea
              id="customization_details" name="customization_details"
              placeholder="Location type, branding requirements, number of locations, special features needed, revenue goals..."
              value={formData.customization_details} onChange={onChange}
              className="mt-1 min-h-[180px]" />
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center">
            {submitting ? "Sending…" : "Submit Quote Request"}
          </button>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── FAQ Item ─────────────────────────────────────────────────────────────── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b border-border"
      style={{ paddingBottom: open ? 16 : 0 }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between py-5 text-left"
        style={{ background: "none", border: "none", cursor: "pointer" }}
      >
        <span className="font-body font-semibold text-sm text-foreground pr-8">{q}</span>
        {open
          ? <ChevronUp className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          : <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
      </button>
      {open && (
        <p className="font-body text-sm text-muted-foreground leading-relaxed pb-5">
          {a}
        </p>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   B2B LANDING PAGE
══════════════════════════════════════════════════════════════════════════ */
export default function B2BLandingPage() {
  const navigate = useNavigate();
  const [quoteOpen, setQuoteOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Navbar onCtaClick={() => setQuoteOpen(true)} ctaLabel="Request a Quote" transparent />

      <B2BQuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />

      {/* ════════════════════════════════════
          HERO (shared hero image, B2B variant)
      ════════════════════════════════════ */}
      <HeroSection
        variant="b2b"
        ctaLabel="Request a Business Quote"
        onCtaClick={() => setQuoteOpen(true)}
      />

      {/* ════════════════════════════════════
          TRUST BAR
      ════════════════════════════════════ */}
      <div style={{ background: "rgba(255,255,255,0.02)", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "24px 0" }}>
        <div className="container-lg">
          <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16">
            {[
              { label: "CE / FCC / UL Certified", icon: ShieldCheck },
              { label: "Ships to 40+ Countries", icon: Truck },
              { label: "1-Year Warranty Included", icon: CheckCircle2 },
              { label: "24hr Quote Turnaround", icon: FileText },
            ].map(({ label, icon: Icon }) => (
              <div key={label} className="flex items-center gap-2.5">
                <Icon className="h-4 w-4 flex-shrink-0" style={{ color: "var(--vb-indigo)" }} />
                <span className="font-body text-sm font-medium text-foreground">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════
          WHO WE SERVE
      ════════════════════════════════════ */}
      <section style={{ background: "#050507", padding: "80px 0" }}>
        <div className="container-lg">
          <div className="text-center mb-14">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
              Who we work with
            </p>
            <h2 className="font-heading text-foreground"
              style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: "clamp(1.8rem, 3vw, 2.8rem)" }}>
              Built for operators, investors, and businesses
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {USE_CASES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="product-card p-6" style={{ borderRadius: 20 }}>
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: "var(--grad-subtle)" }}
                >
                  <Icon className="h-5 w-5" style={{ color: "var(--vb-indigo)" }} />
                </div>
                <h3 className="font-body font-semibold text-base text-foreground mb-3">{title}</h3>
                <p className="font-body text-sm text-muted-foreground leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          HOW IT WORKS — Quote to Order
      ════════════════════════════════════ */}
      <section style={{ background: "rgba(255,255,255,0.02)", padding: "80px 0" }}>
        <div className="container-lg">
          <div className="text-center mb-14">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
              The process
            </p>
            <h2 className="font-heading text-foreground"
              style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: "clamp(1.8rem, 3vw, 2.8rem)" }}>
              From quote to delivered machine —
              <br />every step covered
            </h2>
          </div>

          {/* Timeline grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROCESS_STEPS.map(({ icon: Icon, label, title, body, color }, i) => (
              <div
                key={i}
                className="product-card p-6 flex flex-col gap-4"
                style={{ borderRadius: 20 }}
              >
                {/* Step number + icon */}
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: color + "18" }}
                  >
                    <Icon className="h-4 w-4" style={{ color }} />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {label}
                  </span>
                </div>

                <div>
                  <h3 className="font-body font-semibold text-sm text-foreground mb-2">{title}</h3>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <button
              onClick={() => setQuoteOpen(true)}
              className="btn-primary"
              style={{ height: 54, fontSize: 15, borderRadius: 14, padding: "0 40px" }}
            >
              Start with a Free Quote <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          MARKETPLACE ENTRY
      ════════════════════════════════════ */}
      <section style={{ background: "#050507", padding: "80px 0" }}>
        <div className="container-lg">
          <div
            className="flex flex-col lg:flex-row items-center justify-between gap-8 p-10 lg:p-14"
            style={{
              background: "var(--grad-subtle)",
              borderRadius: 28,
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <div className="max-w-xl">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
                Full catalogue
              </p>
              <h2 className="font-heading text-foreground mb-4"
                style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: "clamp(1.6rem, 2.5vw, 2.4rem)" }}>
                Browse every machine we offer
              </h2>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                Vending machines, arcade equipment, token dispensers, and more — all priced, spec'd, and ready to order. Filter by category, review specs, or request a custom build.
              </p>
            </div>
            <div className="flex flex-col gap-3 flex-shrink-0 w-full lg:w-auto">
              <button
                onClick={() => navigate("/shop")}
                className="btn-primary"
                style={{ height: 54, fontSize: 15, borderRadius: 14, padding: "0 36px", whiteSpace: "nowrap" }}
              >
                Go to Marketplace <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setQuoteOpen(true)}
                className="btn-secondary"
                style={{ height: 48, fontSize: 14, borderRadius: 14, padding: "0 32px", whiteSpace: "nowrap" }}
              >
                Request Bulk Pricing
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          FAQ
      ════════════════════════════════════ */}
      <section style={{ background: "#f8f7ff", padding: "80px 0" }}>
        <div className="container-lg">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
                Common questions
              </p>
              <h2 className="font-heading text-foreground"
                style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: "clamp(1.8rem, 3vw, 2.6rem)" }}>
                Frequently Asked Questions
              </h2>
            </div>
            <div>
              {FAQ.map((item) => (
                <FaqItem key={item.q} q={item.q} a={item.a} />
              ))}
            </div>
            <div className="text-center mt-10">
              <p className="font-body text-sm text-muted-foreground mb-4">
                Still have questions? We're happy to help.
              </p>
              <button
                onClick={() => setQuoteOpen(true)}
                className="btn-secondary"
                style={{ height: 48, fontSize: 14, borderRadius: 14 }}
              >
                Contact our team →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ background: "#050507", color: "#f0f0f5", padding: "48px 0", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="container-lg flex items-center justify-between flex-wrap gap-6">
          <div>
            <p className="font-heading text-base font-semibold mb-1"
              style={{ fontFamily: "'Oswald', sans-serif" }}>Vendbabee</p>
            <p className="font-body text-xs" style={{ color: "rgba(248,247,255,0.4)" }}>
              B2B vending solutions for the US, Canada, EU, and beyond.
            </p>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => setQuoteOpen(true)}
              className="font-body text-xs cursor-pointer"
              style={{ background: "none", border: "none", color: "rgba(248,247,255,0.5)",
                textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}>
              Request a Quote
            </button>
            <p className="font-body text-xs" style={{ color: "rgba(248,247,255,0.25)" }}>
              © {new Date().getFullYear()} Vendbabee. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
